import { generatePresignedUrl } from '../api/file-store';

export const PDF_CONTENT_TYPE = 'application/pdf';

export type PresignedUploadType = 'pdf' | 'image';

export interface UploadWithSource {
  source?: string;
  type?: string;
  description?: string;
}

export interface UploadRemoteInfo {
  normalizedType: string;
  remoteFileName: string;
  remoteKey: string;
  remoteSource: string;
}

export const buildRemoteSourcePath = (remoteKey: string): string => {
  if (!remoteKey) {
    return '';
  }
  return remoteKey.startsWith('/') ? remoteKey : `/${remoteKey}`;
};

export const sanitizeUploadFileName = (fileName: string, fallbackFileName: string): string => {
  if (!fileName) {
    return fallbackFileName;
  }
  return fileName.replace(/[^a-z0-9._() -]/gi, '_');
};

export const inferUploadTypeFromFile = (file: { name?: string; type?: string }): string => {
  const fileName = (file?.name || '').toLowerCase();
  const extension = fileName.split('.').pop() || '';
  if (extension) {
    if (extension === 'jpeg') {
      return 'jpg';
    }
    return extension;
  }

  const mime = (file?.type || '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
  if (mime.includes('png')) return 'png';
  if (mime.includes('svg')) return 'svg';
  if (mime.includes('webp')) return 'webp';
  return 'application/octet-stream';
};

export const inferUploadTypeFromSource = (source: string): string => {
  const extension = (source || '').split('?')[0].split('.').pop()?.toLowerCase();
  if (!extension) return 'application/octet-stream';
  if (extension === 'jpeg') return 'jpg';
  return extension;
};

export const mapStoredUploadToRemoteItem = (item: UploadWithSource) => {
  const source = item?.source || '';
  const normalizedType = (item?.type || inferUploadTypeFromSource(source) || 'application/octet-stream')
    .toString()
    .toLowerCase();
  const fileName = source.split('/').pop() || `file-${Date.now()}`;

  return {
    name: fileName,
    source,
    description: item?.description || '',
    fromRest: true,
    fileType: normalizedType,
    type: normalizedType,
    mimeType: normalizedType === 'pdf' ? PDF_CONTENT_TYPE : normalizedType,
    remoteSource: source,
    remoteKey: source.replace(/^\//, ''),
    remoteUploaded: true,
    isPdf: normalizedType.includes('pdf'),
  };
};

export const mapStoredUploadsToRemoteItems = (items?: UploadWithSource[]) =>
  Array.isArray(items) ? items.map(mapStoredUploadToRemoteItem) : [];

export const uploadFileWithPresign = async ({
  accountId,
  contentType,
  file,
  fileName,
  preserveFileName = true,
  uploadType,
}: {
  accountId: number;
  contentType?: string;
  file: Blob;
  fileName?: string;
  preserveFileName?: boolean;
  uploadType: PresignedUploadType;
}): Promise<UploadRemoteInfo> => {
  if (!accountId) {
    throw new Error('Missing accountId');
  }

  const normalizedType = inferUploadTypeFromFile({
    name: fileName,
    type: contentType || file.type,
  });
  const fallbackName =
    uploadType === 'pdf' ? 'upload.pdf' : `upload.${normalizedType || 'png'}`;
  const sanitizedFileName = sanitizeUploadFileName(fileName || fallbackName, fallbackName);
  const resolvedContentType =
    contentType || file.type || (uploadType === 'pdf' ? PDF_CONTENT_TYPE : 'application/octet-stream');
  const storeId = `${accountId}_${uploadType === 'pdf' ? 'pdfs' : 'images'}`;

  const presignResponse = await generatePresignedUrl({
    storeId,
    fileName: sanitizedFileName,
    method: 'PUT',
    contentType: resolvedContentType,
    preserveFileName,
  });
  const remoteFileName = presignResponse.fileName || sanitizedFileName;

  const response = await fetch(presignResponse.url, {
    method: presignResponse.method || 'PUT',
    headers: {
      'Content-Type': resolvedContentType,
      'Content-Disposition': `attachment; filename="${remoteFileName}"`,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file via presigned URL. Status: ${response.status}`);
  }

  const remoteKey = presignResponse.key;
  return {
    normalizedType,
    remoteFileName,
    remoteKey,
    remoteSource: buildRemoteSourcePath(remoteKey),
  };
};
